import { Fragment, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Switch } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { Link } from 'react-router-dom';
import {
  getReviewsWidgetAdmin, scanGatherUp, saveReviewsWidgetClient, acceptReviewsWidgetSuggestions, previewReviewsWidget, syncReviewsWidgetNow,
  type ReviewsWidgetAdmin, type ReviewsWidgetClient, type ReviewsWidgetFile,
} from '@/data/client';
import { adminClientPath } from './data';
import { apiErrMsg } from './util';

/* Reviews widget setup — the Grade.us replacement. Match each client to its
   GatherUp agency client, preview the reviews.json it would get, then switch it
   on: only then does the daily sync write src/data/reviews.json to its repo.
   Nothing on this tab changes the reviewsIQ dashboard. */
type Filter = 'todo' | 'matched' | 'on' | 'problems' | 'all';
const KEY = ['admin', 'reviews-widget'];

function hasProblem(c: ReviewsWidgetClient): boolean {
  return c.agentMissing || c.duplicateAgent || !!c.coverage?.missingFeeds.length || !!c.coverage?.extraBusinesses.length || (c.enabled && !!c.sync.lastError);
}
const when = (iso: string | null) => (iso ? new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`).toLocaleString() : '');

export function ReviewsWidgetBoard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: KEY, queryFn: getReviewsWidgetAdmin });
  const [filter, setFilter] = useState<Filter>('matched');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });

  const run = async (fn: () => Promise<string>) => {
    setBusy(true); setMsg(null);
    try { setMsg(await fn()); refresh(); } catch (e) { setMsg(apiErrMsg(e, 'Failed.')); } finally { setBusy(false); }
  };

  const clients = data?.clients ?? [];
  const counts = useMemo(() => ({
    todo: clients.filter((c) => c.agentId == null).length,
    matched: clients.filter((c) => c.agentId != null).length,
    on: clients.filter((c) => c.enabled).length,
    problems: clients.filter(hasProblem).length,
    suggested: clients.filter((c) => c.agentId == null && c.suggestion?.reason === 'business').length,
  }), [clients]);
  const rows = clients.filter((c) =>
    filter === 'all' ? true : filter === 'todo' ? c.agentId == null : filter === 'matched' ? c.agentId != null : filter === 'on' ? c.enabled : hasProblem(c));

  return (
    <div className="dash-stack">
      <div>
        <h2>Reviews widget</h2>
        <p>
          The Grade.us replacement: each client site gets its reviews as plain HTML at build time, with the source logo on every review.
          Match a client to its GatherUp client, <strong>Preview</strong> what its site would get, then switch it on — only switched-on
          clients are synced (daily) and have <code>src/data/reviews.json</code> written to their repo. Nothing here affects reviewsIQ.
        </p>
      </div>

      {data && !data.configured && (
        <p className="admin-modal__err">GatherUp API credentials (GATHERUP_TOKEN, GATHERUP_CLIENT_ID) are not set on this Worker.</p>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button size="sm" variant="outline" disabled={busy || !data?.configured} iconLeft={<Icon name="refresh-cw" size={14} />}
          onClick={() => run(async () => {
            const r = await scanGatherUp();
            return `Scanned ${r.scanned} of ${r.agents} GatherUp clients.${r.failed.length ? ` ${r.failed.length} failed: ${r.failed.map((f) => f.name).join(', ')}` : ''}`;
          })}>
          {busy ? 'Working…' : 'Scan GatherUp'}
        </Button>
        {counts.suggested > 0 && (
          <Button size="sm" variant="outline" disabled={busy} iconLeft={<Icon name="check" size={14} />}
            onClick={() => run(async () => `Matched ${(await acceptReviewsWidgetSuggestions()).applied} clients by business id.`)}>
            Accept {counts.suggested} business-id matches
          </Button>
        )}
        <span className="dash-muted" style={{ fontSize: 'var(--text-sm)' }}>
          {data?.scannedAt ? `Last scan ${new Date(data.scannedAt).toLocaleString()}` : 'Not scanned yet — scan to load GatherUp’s client list.'}
        </span>
      </div>
      {msg && <p className="dash-muted">{msg}</p>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {([['matched', `Matched (${counts.matched})`], ['on', `On (${counts.on})`], ['todo', `Not matched (${counts.todo})`], ['problems', `Check (${counts.problems})`], ['all', 'All']] as [Filter, string][]).map(([v, label]) => (
          <Button key={v} size="sm" variant={filter === v ? 'primary' : 'outline'} onClick={() => setFilter(v)}>{label}</Button>
        ))}
      </div>

      {isLoading ? <p className="dash-muted">Loading…</p> : error ? <p className="admin-modal__err">{apiErrMsg(error, 'Could not load.')}</p> : rows.length === 0 ? (
        <p className="dash-muted">Nothing in this view.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="dash-table">
            <thead><tr><th>Client</th><th>GatherUp client</th><th>Locations</th><th>Min stars</th><th>On the site</th></tr></thead>
            <tbody>{rows.map((c) => <Row key={c.orgId} c={c} agents={data!.agents} onChanged={refresh} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ c, agents, onChanged }: { c: ReviewsWidgetClient; agents: ReviewsWidgetAdmin['agents']; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<ReviewsWidgetFile | null>(null);
  const act = async (fn: () => Promise<string | void>) => {
    setBusy(true); setMsg(null);
    try { const m = await fn(); if (m) setMsg(m); onChanged(); } catch (e) { setMsg(apiErrMsg(e, 'Failed.')); } finally { setBusy(false); }
  };
  const save = (body: { agentId?: number | null; minRating?: number; enabled?: boolean }) => act(async () => { await saveReviewsWidgetClient(c.orgId, body); });
  const small = { fontSize: 'var(--text-xs)' } as const;

  return (
    <Fragment>
      <tr>
        <td>
          <Link to={adminClientPath(c.orgId, 'profile')}><strong>{c.name}</strong></Link>
          <div className="dash-muted" style={small}>
            {c.locations.length ? `${c.locations.length} location${c.locations.length > 1 ? 's' : ''} in tritonIQ` : 'No GatherUp feed on file'}
          </div>
        </td>
        <td style={{ minWidth: 280 }}>
          <select className="tr-select" value={c.agentId ?? ''} disabled={busy}
            onChange={(e) => save({ agentId: e.target.value ? Number(e.target.value) : null })}>
            <option value="">— not matched —</option>
            {agents.map((a) => <option key={a.agentId} value={a.agentId}>{a.name} ({a.businessCount})</option>)}
          </select>
          {c.agentId == null && c.suggestion && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
              <Badge variant={c.suggestion.reason === 'business' ? 'success' : 'warning'}>
                {c.suggestion.reason === 'business' ? 'Business id match' : 'Name match — check'}
              </Badge>
              <span style={small}>{c.suggestion.agentName}</span>
              <button type="button" className="admin-linkbtn" disabled={busy} onClick={() => save({ agentId: c.suggestion!.agentId })}>Use</button>
            </div>
          )}
          {c.agentId == null && !c.suggestion && c.ambiguous.length > 0 && (
            <div className="dash-muted" style={small}>Several GatherUp clients could match — pick one.</div>
          )}
          {c.agentMissing && <div className="admin-modal__err" style={small}>Not in the last GatherUp scan.</div>}
          {c.duplicateAgent && <div className="admin-modal__err" style={small}>Also matched to another client.</div>}
        </td>
        <td style={small}>
          {!c.coverage ? <span className="dash-muted">—</span> : !c.coverage.missingFeeds.length && !c.coverage.extraBusinesses.length ? (
            <Badge variant="success" dot>All locations line up</Badge>
          ) : (
            <div style={{ display: 'grid', gap: 4 }}>
              {c.coverage.missingFeeds.map((id) => (
                <div key={id}>
                  <Badge variant="warning">Not in GatherUp client</Badge>{' '}
                  {c.locations.find((l) => l.feedId === id)?.label ?? id} ({id})
                </div>
              ))}
              {c.coverage.extraBusinesses.map((b) => (
                <div key={b.id}>
                  <Badge variant="outline">No tritonIQ location</Badge> {b.name}{b.city ? `, ${b.city}` : ''} ({b.id})
                </div>
              ))}
            </div>
          )}
        </td>
        <td>
          <select className="tr-select" value={c.minRating} disabled={busy} onChange={(e) => save({ minRating: Number(e.target.value) })} style={{ width: 80 }}>
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}★+</option>)}
          </select>
        </td>
        <td style={{ minWidth: 260 }}>
          {c.agentId == null ? <span className="dash-muted" style={small}>Match first.</span> : (
            <div style={{ display: 'grid', gap: 6 }}>
              <div title={c.blockers.join(' ')}>
                <Switch label={c.enabled ? 'On' : 'Off'} checked={c.enabled} disabled={busy || (!c.enabled && c.blockers.length > 0)}
                  onChange={(e) => save({ enabled: e.target.checked })} />
              </div>
              {!c.enabled && c.blockers.length > 0 && <div className="dash-muted" style={small}>{c.blockers.join(' ')}</div>}
              {c.enabled && <SyncStatus c={c} />}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="admin-linkbtn" disabled={busy}
                  onClick={() => preview ? setPreview(null) : act(async () => { setPreview(await previewReviewsWidget(c.orgId)); })}>
                  {preview ? 'Hide preview' : 'Preview'}
                </button>
                {c.enabled && (
                  <button type="button" className="admin-linkbtn" disabled={busy}
                    onClick={() => act(async () => {
                      const r = await syncReviewsWidgetNow(c.orgId);
                      return r.unchanged ? `No change — ${r.reviews} reviews already on the site.` : `Written: ${r.reviews} reviews.`;
                    })}>
                    Sync now
                  </button>
                )}
              </div>
            </div>
          )}
          {busy && <div className="dash-muted" style={small}>Working…</div>}
          {msg && <div className="dash-muted" style={small}>{msg}</div>}
        </td>
      </tr>
      {preview && (
        <tr>
          <td colSpan={5}><Preview file={preview} /></td>
        </tr>
      )}
    </Fragment>
  );
}

function SyncStatus({ c }: { c: ReviewsWidgetClient }) {
  const s = c.sync;
  const small = { fontSize: 'var(--text-xs)' } as const;
  if (s.lastError) return <div className="admin-modal__err" style={small}>Last sync failed {when(s.lastAttemptAt)}: {s.lastError}</div>;
  if (!s.lastOkAt) return <div className="dash-muted" style={small}>Waiting for the first sync (within the hour), or Sync now.</div>;
  return (
    <div className="dash-muted" style={small}>
      {s.reviewCount ?? 0} reviews · checked {when(s.lastOkAt)}
      {s.lastPushedAt && <> · written {when(s.lastPushedAt)}{s.lastPushedUrl && <> (<a href={s.lastPushedUrl} target="_blank" rel="noreferrer">file</a>)</>}</>}
    </div>
  );
}

/* What the site will get: the numbers per location, a few reviews rendered
   roughly the way the component will, and the raw file for the site team. */
function Preview({ file }: { file: ReviewsWidgetFile }) {
  const small = { fontSize: 'var(--text-xs)' } as const;
  const locs = Object.entries(file.locations);
  return (
    <div className="rw-prev">
      <div>
        <strong>{file.all.rating}★ from {file.all.count} reviews</strong>
        <span className="dash-muted"> · {file.reviews.length} shown at {file.minRating}★ and up</span>
        {file.all.links.read && <> · <a href={file.all.links.read} target="_blank" rel="noreferrer">Read all on Google</a></>}
      </div>
      {locs.length > 1 && (
        <div style={small}>
          {locs.map(([id, l]) => (
            <div key={id}>{l.label}{l.primary ? ' (primary)' : ''} — {l.rating}★, {l.count} reviews, {file.reviews.filter((r) => r.location === id).length} shown · <code>location="{id}"</code></div>
          ))}
        </div>
      )}
      <div className="rw-prev__grid">
        {file.reviews.slice(0, 6).map((r) => (
          <div key={r.id} className="rw-prev__card">
            <div className="rw-prev__head">
              <span className="rw-prev__stars" aria-label={`${r.rating} out of 5 stars`}>{'★'.repeat(r.rating)}</span>
              <img src={file.sources[r.source]?.logo} alt={file.sources[r.source]?.label} height={18} />
            </div>
            <div style={small}><strong>{r.author}</strong> – {r.dateLabel}</div>
            <div style={{ fontSize: 'var(--text-sm)' }}>{r.text.length > 220 ? `${r.text.slice(0, 220)}…` : r.text}</div>
            {r.reply && <div className="dash-muted" style={small}>Reply: {r.reply.text.slice(0, 120)}{r.reply.text.length > 120 ? '…' : ''}</div>}
          </div>
        ))}
      </div>
      <details>
        <summary style={small}>reviews.json ({Math.round(JSON.stringify(file).length / 1024)} KB)</summary>
        <pre className="rw-prev__json">{JSON.stringify(file, null, 2)}</pre>
      </details>
    </div>
  );
}
